import * as Dialog from "@radix-ui/react-dialog";
import { X } from "lucide-react";

import { type RunError, type RunSummary, useFeedRun } from "~/lib/feeds";
import { TableSkeleton } from "./TableSkeleton";

interface Props {
    slug: string;
    run: RunSummary | null;
    open: boolean;
    onOpenChange: (o: boolean) => void;
}

function statusPill(s: string): string {
    if (s === "ok") return "status-pill--live";
    if (s === "partial") return "status-pill--paused";
    if (s === "error") return "status-pill--failed";
    return "status-pill--queued";
}

function formatDuration(startISO: string, endISO?: string): string {
    if (!endISO) return "running…";
    const d = new Date(endISO).getTime() - new Date(startISO).getTime();
    if (d < 0) return "—";
    if (d < 1000) return `${d}ms`;
    if (d < 60_000) return `${(d / 1000).toFixed(1)}s`;
    return `${Math.floor(d / 60_000)}m ${Math.floor((d % 60_000) / 1000)}s`;
}

export function RunDetailDialog({ slug, run, open, onOpenChange }: Props): React.ReactElement {
    const detail = useFeedRun(slug, run?.id);

    return (
        <Dialog.Root open={open} onOpenChange={onOpenChange}>
            <Dialog.Portal>
                <Dialog.Overlay className="dialog-overlay">
                    <Dialog.Content
                        className="dialog-content enter-up"
                        style={{
                            padding: "var(--space-6)",
                            maxWidth: 900,
                            width: "min(900px, 100vw)",
                            maxHeight: "90vh",
                            overflow: "auto",
                        }}
                    >
                        <div className="cluster-xs" style={{ justifyContent: "space-between" }}>
                            <Dialog.Title className="type-h2">Run detail</Dialog.Title>
                            <Dialog.Close asChild>
                                <button type="button" className="btn btn--ghost btn--sm" aria-label="Close">
                                    <X size={14} />
                                </button>
                            </Dialog.Close>
                        </div>

                        {!run && <div className="text-13 text-text-muted">No run selected.</div>}
                        {run && (
                            <Dialog.Description asChild>
                                <div className="stack-sm" style={{ marginTop: "var(--space-4)" }}>
                                    <div className="cluster-sm text-12 text-text-muted">
                                        <code className="type-mono-sm">{run.id}</code>
                                        <span className="chip">{run.trigger}</span>
                                        <span className={`status-pill ${statusPill(run.status)}`}>{run.status}</span>
                                        <span className="tabular-nums">
                                            {run.rowsIn.toLocaleString()} → {run.rowsOut.toLocaleString()}
                                        </span>
                                        <span className="tabular-nums">
                                            {formatDuration(run.startedAt, run.finishedAt)}
                                        </span>
                                    </div>

                                    <h3 className="type-h3" style={{ marginTop: "var(--space-4)" }}>Errors</h3>
                                    {detail.isLoading && <TableSkeleton rows={5} columns={4} />}
                                    {detail.data && detail.data.errors.length === 0 && (
                                        <div className="empty-state">
                                            <div className="empty-state__title">No errors in this run</div>
                                        </div>
                                    )}
                                    {detail.data && detail.data.errors.length > 0 && (
                                        <div style={{ overflowX: "auto" }}>
                                            <table className="data-table">
                                                <thead>
                                                    <tr>
                                                        <th style={{ width: 1, whiteSpace: "nowrap" }}>Row</th>
                                                        <th style={{ width: 1, whiteSpace: "nowrap" }}>Stage</th>
                                                        <th style={{ width: 1, whiteSpace: "nowrap" }}>Field</th>
                                                        <th>Reason</th>
                                                        <th>Excerpt</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {detail.data.errors.map((e, i) => (
                                                        <ErrorRow key={i} err={e} />
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                            </Dialog.Description>
                        )}
                    </Dialog.Content>
                </Dialog.Overlay>
            </Dialog.Portal>
        </Dialog.Root>
    );
}

function ErrorRow({ err }: { err: RunError }): React.ReactElement {
    return (
        <tr>
            <td className="tabular-nums text-12">{err.rowIndex}</td>
            <td><span className="chip">{err.stage}</span></td>
            <td className="type-mono-sm text-12">{err.field ?? "—"}</td>
            <td style={{ maxWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {err.reason}
            </td>
            <td
                className="type-mono-sm text-12 text-text-muted"
                style={{ maxWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
            >
                {err.excerpt}
            </td>
        </tr>
    );
}
